import { z } from "zod";
/**
 * Central enum definitions shared by the DB schema, the API validators and the
 * web client. Each is exported both as a readonly tuple (for Drizzle pgEnum)
 * and as a Zod enum (for request validation).
 */
export const USER_STATUSES = ["ghost", "active", "disabled"];
export const userStatusSchema = z.enum(USER_STATUSES);
export const AUTH_PROVIDERS = ["password", "google", "apple"];
export const authProviderSchema = z.enum(AUTH_PROVIDERS);
export const ACCOUNT_TYPES = ["cash", "bank", "wallet", "savings", "other"];
export const accountTypeSchema = z.enum(ACCOUNT_TYPES);
// Who can discover an account by the owner's username.
//  private     = nobody but the owner
//  connections = the owner's accepted connections
//  public      = anyone who knows the username
export const ACCOUNT_VISIBILITIES = ["private", "connections", "public"];
export const accountVisibilitySchema = z.enum(ACCOUNT_VISIBILITIES);
// Mutual (friend-request style) connection between two users.
export const CONNECTION_STATUSES = ["pending", "accepted", "rejected"];
export const connectionStatusSchema = z.enum(CONNECTION_STATUSES);
export const CATEGORY_KINDS = ["income", "expense"];
export const categoryKindSchema = z.enum(CATEGORY_KINDS);
export const TRANSACTION_TYPES = [
    "income",
    "expense",
    "transfer",
    "loan_disburse",
    "loan_repayment",
    // Reverses a loan disbursement when a loan is undone (added by mistake).
    "loan_reversal",
    "committee_contribution",
    "committee_payout",
];
export const transactionTypeSchema = z.enum(TRANSACTION_TYPES);
export const TRANSACTION_STATUSES = ["pending", "confirmed", "cancelled"];
export const transactionStatusSchema = z.enum(TRANSACTION_STATUSES);
export const LEDGER_DIRECTIONS = ["debit", "credit"];
export const ledgerDirectionSchema = z.enum(LEDGER_DIRECTIONS);
export const WITNESS_STATUSES = ["pending", "confirmed", "declined"];
export const witnessStatusSchema = z.enum(WITNESS_STATUSES);
// pending      = awaiting counterparty acceptance (and witnesses, if required);
//                nothing is posted to the ledger yet.
// active       = accepted (+ witnessed) and disbursed.
// undo_pending = an active loan whose disbursement is being reversed (added by
//                mistake); awaiting the counterparty (and any witnesses) to
//                re-approve before the reversal posts.
// rejected     = counterparty declined.
// cancelled    = initiator withdrew before acceptance, or the loan was undone.
export const LOAN_STATUSES = [
    "pending",
    "active",
    "undo_pending",
    "repaid",
    "overdue",
    "rejected",
    "cancelled",
];
export const loanStatusSchema = z.enum(LOAN_STATUSES);
// A loan repayment is a two-sided request → accept: one party proposes a
// repayment (picking their own account), the other confirms (picking theirs),
// then both ledger legs post.
export const LOAN_REPAYMENT_STATUSES = ["pending", "accepted", "rejected", "cancelled"];
export const loanRepaymentStatusSchema = z.enum(LOAN_REPAYMENT_STATUSES);
export const CADENCES = ["weekly", "biweekly", "monthly", "quarterly", "yearly"];
export const cadenceSchema = z.enum(CADENCES);
export const COMMITTEE_PAYOUT_MODES = ["fixed", "agreed", "lottery"];
export const committeePayoutModeSchema = z.enum(COMMITTEE_PAYOUT_MODES);
export const COMMITTEE_STATUSES = ["forming", "active", "completed"];
export const committeeStatusSchema = z.enum(COMMITTEE_STATUSES);
export const COMMITTEE_MEMBER_STATUSES = ["invited", "accepted", "declined"];
export const committeeMemberStatusSchema = z.enum(COMMITTEE_MEMBER_STATUSES);
export const COMMITTEE_ROUND_STATUSES = ["pending", "collecting", "paid"];
export const committeeRoundStatusSchema = z.enum(COMMITTEE_ROUND_STATUSES);
export const NOTIFICATION_TYPES = [
    "witness_invite",
    "witness_confirmed",
    "witness_declined",
    "transaction_received",
    "connection_request",
    "connection_accepted",
    "loan_request",
    "loan_accepted",
    "loan_rejected",
    "loan_witness_needed",
    "loan_repayment_request",
    "loan_repayment_accepted",
    "loan_repayment_rejected",
    "loan_undo_request",
    "loan_undone",
    "loan_due_soon",
    "loan_overdue",
    "committee_invite",
    "committee_accepted",
    "committee_declined",
    "committee_round_due",
    "committee_payout",
    "recurring_due",
    "system",
];
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export const AI_INSIGHT_KINDS = ["invest", "budget"];
export const aiInsightKindSchema = z.enum(AI_INSIGHT_KINDS);
export const FINANCIAL_EVENT_TYPES = [
    "income",
    "expense",
    "transfer",
    "gift_sent",
    "gift_received",
    "loan_requested",
    "loan_accepted",
    "loan_declined",
    "loan_disbursement_submitted",
    "loan_disbursement_confirmed",
    "loan_repayment_submitted",
    "loan_repayment_confirmed",
    "loan_settled",
    "bill_paid",
    "liability_added",
    "liability_payment",
    "balance_adjustment",
    "ai_answer",
];
export const financialEventTypeSchema = z.enum(FINANCIAL_EVENT_TYPES);
export const FINANCIAL_EVENT_STATUSES = [
    "draft",
    "pending_confirmation",
    "confirmed",
    "rejected",
    "disputed",
    "cancelled",
    "reversed",
];
export const financialEventStatusSchema = z.enum(FINANCIAL_EVENT_STATUSES);
export const CONFIRMATION_POLICIES = ["none", "counterparty_required", "both_required"];
export const confirmationPolicySchema = z.enum(CONFIRMATION_POLICIES);
export const TIMELINE_SCOPES = ["global", "account", "person", "loan"];
export const timelineScopeSchema = z.enum(TIMELINE_SCOPES);
//# sourceMappingURL=enums.js.map